import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Mail, Edit, RefreshCw, Send, Eye, Save, X, 
  UserPlus, FileText, CheckCircle, AlertTriangle, KeyRound
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const TEMPLATE_ICONS = {
  welcome: UserPlus,
  offer_available: FileText,
  offer_signed: CheckCircle,
  document_rejected: AlertTriangle,
  otp_verification: KeyRound
};

const TEMPLATE_COLORS = {
  welcome: "bg-blue-100 text-blue-700",
  offer_available: "bg-purple-100 text-purple-700",
  offer_signed: "bg-green-100 text-green-700",
  document_rejected: "bg-red-100 text-red-700",
  otp_verification: "bg-amber-100 text-amber-700"
};

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editForm, setEditForm] = useState({ subject: "", body_html: "" });
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const getToken = () => localStorage.getItem("admin_token");
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` }});

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/email-templates`, authHeaders());
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      subject: template.subject,
      body_html: template.body_html
    });
    setShowEditModal(true);
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const handleTestEmail = (template) => {
    setSelectedTemplate(template);
    setTestEmail("");
    setShowTestModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("subject", editForm.subject);
      formData.append("body_html", editForm.body_html);

      await axios.put(
        `${API}/email-templates/${selectedTemplate.template_type}`,
        formData,
        authHeaders()
      );
      toast.success("Template saved successfully");
      setShowEditModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (template) => {
    if (!window.confirm("Reset this template to default? Your customizations will be lost.")) return;
    
    try {
      await axios.post(`${API}/email-templates/${template.template_type}/reset`, {}, authHeaders());
      toast.success("Template reset to default");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to reset template");
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error("Please enter an email address");
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("template_type", selectedTemplate.template_type);
      formData.append("test_email", testEmail);

      await axios.post(`${API}/email-templates/test`, formData, authHeaders());
      toast.success(`Test email sent to ${testEmail}`);
      setShowTestModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSending(false);
    }
  };

  const formatTemplateName = (type) => {
    return type.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a8a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Email Templates</h1>
          <p className="text-slate-600">Customize automated email templates for candidate communications</p>
        </div>
        <Button variant="outline" onClick={fetchTemplates}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-6">
        {templates.map((template) => {
          const Icon = TEMPLATE_ICONS[template.template_type] || Mail;
          const colorClass = TEMPLATE_COLORS[template.template_type] || "bg-slate-100 text-slate-700";

          return (
            <Card key={template.template_type} data-testid={`template-${template.template_type}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {formatTemplateName(template.template_type)}
                        {template.is_default && (
                          <Badge variant="outline" className="text-xs">Default</Badge>
                        )}
                      </CardTitle>
                      <CardDescription>{template.description}</CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handlePreview(template)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleTestEmail(template)}>
                      <Send className="w-4 h-4" />
                    </Button>
                    {!template.is_default && (
                      <Button variant="ghost" size="sm" onClick={() => handleReset(template)} className="text-amber-600">
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Subject</p>
                    <p className="text-sm text-slate-900 font-mono bg-slate-50 p-2 rounded">{template.subject}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Available Variables</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {template.variables?.map((v) => (
                        <Badge key={v} variant="secondary" className="font-mono text-xs">
                          {`{{${v}}}`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Template: {selectedTemplate && formatTemplateName(selectedTemplate.template_type)}</DialogTitle>
            <DialogDescription>
              Customize the email content. Use variables like {`{{candidate_name}}`} to personalize emails.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={editForm.subject}
                onChange={(e) => setEditForm({...editForm, subject: e.target.value})}
                placeholder="Email subject"
                data-testid="template-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Available Variables</Label>
              <div className="flex flex-wrap gap-2">
                {selectedTemplate?.variables?.map((v) => (
                  <Badge 
                    key={v} 
                    variant="outline" 
                    className="font-mono text-xs cursor-pointer hover:bg-slate-100"
                    onClick={() => {
                      navigator.clipboard.writeText(`{{${v}}}`);
                      toast.success(`Copied {{${v}}} to clipboard`);
                    }}
                  >
                    {`{{${v}}}`}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Body (HTML)</Label>
              <Textarea
                value={editForm.body_html}
                onChange={(e) => setEditForm({...editForm, body_html: e.target.value})}
                placeholder="Enter HTML content"
                className="font-mono text-sm min-h-[400px]"
                data-testid="template-body-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1e3a8a] hover:bg-[#172554]">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview: {selectedTemplate && formatTemplateName(selectedTemplate.template_type)}</DialogTitle>
            <DialogDescription>
              This is how the email will appear to recipients
            </DialogDescription>
          </DialogHeader>

          <div className="border rounded-lg overflow-hidden">
            <div className="bg-slate-100 p-3 border-b">
              <p className="text-sm"><strong>Subject:</strong> {selectedTemplate?.subject}</p>
            </div>
            <div 
              className="p-4 bg-white"
              dangerouslySetInnerHTML={{ __html: selectedTemplate?.body_html || "" }}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Modal */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email to verify the template looks correct
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address"
                data-testid="test-email-input"
              />
            </div>
            <p className="text-sm text-slate-500">
              A test email will be sent with sample data for all variables.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendTest} disabled={sending} className="bg-[#1e3a8a] hover:bg-[#172554]">
              <Send className="w-4 h-4 mr-2" />
              {sending ? "Sending..." : "Send Test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailTemplates;
